import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import dayjs from 'dayjs';
import DateRangePicker from "../common/DateRangePicker";
import "./MainV2.css";
import NoDevicesBanner from "./NoDevicesBanner";
import ScheduleStrip from "./ScheduleStrip";
import { isToBeScheduled } from "../DeviceSchedule/function";
import { deviceTypeGroups } from "./selection";
import StoreApplianceSummary from "./StoreApplianceSummary";
import { messages } from "../common/notification";
import { fetchStoreAssets } from "../redux/action/stores";
import { useStoreListRefresh } from "../common/useStoreList";
import { useSearchOnOpen } from "../common/useSearchOnOpen";
import {
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Row, Col } from "react-bootstrap";
import { Toaster } from "react-hot-toast";
import { calculateGlobalData, calculateTime } from "../common/function";
import CarbonConsumeBarChart from "../Chart/BarCharts/CarbonConsumeBarChart";
import MoneySavedBarChart from "../Chart/BarCharts/MoneySavedBarChart";
import CarbonSavedBarChart from "../Chart/BarCharts/CarbonSavedBarChart";
import UnitConsumedIntervalLineChart from "../Chart/LineCharts/UnitConsumedIntervalLineChart";
import MoneySpentBarChart from "../Chart/BarCharts/MoneySpentBarChart";
import OnlineOfflineBarChart from "../Chart/BarCharts/OnlineOfflineBarChart";
import CurrentLineChart from "../Chart/LineCharts/CurrentLineChart";
import VoltageLineChart from "../Chart/LineCharts/VoltageLineChart";
import TemperatureLineChart from "../Chart/LineCharts/TemperatureLineChart";
import PowerConsumptionLineChart from "../Chart/LineCharts/PowerConsumptionLineChart";
import UnitSavingsDetailContainer from "../SystemValues/UnitSavings";
import VoltageDetailContainer from "../SystemValues/Voltage";
import PowerFactorDetailContainer from "../SystemValues/PowerFactor";
import ACStatusDetailContainer from "../SystemValues/ACStatus";
import OnlineStatusDetailContainer from "../SystemValues/OnlineStatus";
import ApplianceHealthDetailContainer from "../SystemValues/ApplianceHealth";
import UnitConsumedDetailContainer from "../SystemValues/UnitConsumed";
import UnitSavedDetailContainer from "../SystemValues/UnitSaved";
import MoneySavedDetailContainer from "../SystemValues/MoneySaved";
import MoneySpentDetailContainer from "../SystemValues/MoneySpent";
import CarbonSavedDetailContainer from "../SystemValues/CarbonSaved";
import MoneySavingDetailContainer from "../SystemValues/MoneySaving";
import store from "../redux";
import {
  selectedDeviceType,
  selectedRoomDevices,
  selectedDeviceFromDropdown,
  selectedHouse,
} from "../redux/action/deviceDropdownSelection";
import { selectedDay, selectedEndDay } from "../redux/action/selectedDate";
import styles from "./common/styles";
import {
  fetchAndStoreMultipleDevicesTodayData,
  fetchAndStoreMulipleDevicesSelectedDayData,
} from "../redux/action/totalData";
import UnitSavedBarChart from "../Chart/BarCharts/UnitSavedBarChart";
import { selectedDeviceData } from "../redux/actionTypes";
import UnitConsumedBarChart from "../Chart/BarCharts/UnitConsumedBarChart";
import PowerDetailContainer from "../SystemValues/Power";
import CurrentDetailContainer from "../SystemValues/Current";
import TemperatureDetailContainer from "../SystemValues/Temperature";
import TotalUnitsConsumedDetailContainer from "../SystemValues/TotalUnitsConsumed";
import { config } from "../../config";
import { images } from "../common/images";
import { deviceDetails, environAwsApi, thermoAwsApi, weatherAwsApi } from "../common/ApiCall";
import WeatherControllog from "../Chart/LineCharts/WeatherControllog";
import WeattherValue from "../SystemValues/WeattherValue";
import ThermoSmartStateSystemValue from "../ThermoSmartStateSystemValue";
import SavingsDetailContainer from "../SystemValues/Saving";
import SavingBarChart from "../Chart/BarCharts/Savings";
import DoorSensor from "../SystemValues/DoorSensor";
import DoorSensorTable from "./DoorSensorTable";
import { findDatesBetween } from "../redux/action/dailyUnit";
import { notShowSystemValuesWhenMultiDay } from "./constants";
import { formatDuration, formatDurationHrsMinsSecs } from "../Settings/SettingsNavigation/Report/function";
import ApplianceOnTimeDetailContainer from "../SystemValues/ApplianceOnTimeDetailContainer";
import EnergiSychOccupancyLogs from "./EnergiSychOccupancyLogs";
import CumMoneySpentBarChart from "../Chart/BarCharts/CumMoneySpentBarChart";
import CumUnitConsumedBarChart from "../Chart/BarCharts/CumUnitConsumedBarChart";
import CumCarbonConsumeBarChart from "../Chart/BarCharts/CumCarbonConsumeBarChart";
import FrequencyLineChart from "../Chart/LineCharts/FrequencyLineChart";
import FrequencyDetailContainer from "../SystemValues/FrequencyDetailContainer";
import NetKwhKvah from "../Chart/BarCharts/NetKwhKvah";
import PowerFactorLineChart from "../Chart/LineCharts/PowerFactorLineChart";


// The per-phase reading tiles, by the system value key that turns each on.
const PHASE_READING_TILES = {
  voltage: VoltageDetailContainer,
  frequency: FrequencyDetailContainer,
  current: CurrentDetailContainer,
  powerFactor: PowerFactorDetailContainer,
  power: PowerDetailContainer,
};

// System value tiles never shown, even when a device's config lists them.
// Remove a key to bring its tile back.
const HIDDEN_SYSTEM_VALUES = ["applianceHealth"];

// The tiles and charts for M61 (single-phase) and M63 (three-phase) meters,
// used when their /details document leaves graphParameters or systemValues
// out. Each field falls back on its own, so a device that sets one keeps it.
const METER_DEFAULT_CONFIG = {
  graphParameters: {
    current: {},
    frequency: {},
    power: {},
    powerFactor: {},
    unitConsumed: { xAxisInterval: 1 },
    voltage: {},
  },
  systemValues: [
    "voltage",
    "powerFactor",
    "power",
    "current",
    "onlineStatus",
    "ACStatus",
    "applianceHealth",
    "unitConsumed",
  ],
};
const hasMeterDefaults = (deviceId) => /^M6[13]/.test(deviceId ?? "");

// The filter bar's MUI fields, matched to the 40px boxes used on the other
// pages. App.css centres text app-wide, so the values are put back left.
const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    fontFamily: "Manrope, sans-serif",
    fontSize: 14,
    textAlign: "left",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E9ECEF" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#C7D0E0",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#2050FF",
    borderWidth: 1,
  },
  "& .MuiSelect-select": { textAlign: "left" },
};

function MainV2({ setLoading, setRoomCount, roomCount }) {
  //----------------------------------------Redux data----------------------------------------------------
  const houseData = useSelector((state) => state.houseData);
  const { houseId, selectedDeviceDetails, deviceType, devices } = useSelector(
    (state) => state.dropdownSelectionData || {}
  );

  const selectedDayData = useSelector((state) => state.selectedDate);
  const selectedDate = useSelector((state) => state.selectedDate);
  const selectedEndDate = useSelector((state) => state.selectedEndDay);
  const snapshotUnit = useSelector((state) => state.snapshotUnit);
  const housesList = useSelector((state) => state.housesList);


  //----------------------------------------Use state----------------------------------------------------
  const [selectedDeviceSelectedDayData, setSelectedDeviceSelectedDayData] = useState([]); // most important variable as from this only all the data of the selected device is used
  const [systemValuesData, setSystemValuesData] = useState({
    globalMoneySaved: 0,
    globalMoneySpent: 0,
    globalUnitSaved: 0,
    globalUnitConsumed: 0,
    globalUnitSavingPercentge: 0,
    globalSaving: 0,
    globalNetUnitConsumed:0,
    globalNetMoneySpent:0,
    globalNetKwh:0,
    globalNetKvah:0,
    time: "12:00 AM",
  });

  let ppu =
    houseData.ppu !== undefined && houseData.ppu > 0 ? houseData.ppu : 15;

  const [graphData, setGraphData] = useState({
    moneySaved: [],
    moneySpent: [],
    online: [],
    carbonConsumed: [],
    carbonSaved: [],
    unitConsumed: [],
    unitSaved: [],
    saving: [],
    netUnitConsumedData: [],
  });

  const [phaseLoadLogs, setPhaseLoadLogs] = useState({});

  // The device document from POST /details, which is what carries
  // graphParameters and systemValues - the assets list the dropdowns are built
  // from has neither. Kept apart from selectedDeviceDetails because the
  // appliance dropdown matches on JSON.stringify of it, so merging fields in
  // would leave no option selected.
  const [deviceDocument, setDeviceDocument] = useState({ deviceId: undefined, data: {} });

  useEffect(() => {
    const deviceId = selectedDeviceDetails?.deviceId;
    if (!deviceId) return;
    let cancelled = false;
    deviceDetails(deviceId).then((data) => {
      // A slower response for a device the user has already moved off of
      // must not overwrite the current one.
      if (!cancelled) setDeviceDocument({ deviceId, data: data || {} });
    });
    return () => {
      cancelled = true;
    };
    // Re-read on a date change too, so the Appliance Status tile's on/off
    // (applianceState) is current rather than as of when the device was picked.
    // The old document stays shown until the new one arrives.
  }, [selectedDeviceDetails?.deviceId, selectedDate, selectedEndDate]);

  // True once /details has answered for the selected device. Until then the
  // charts and tiles wait, rather than briefly rendering all of them (which is
  // what a missing graphParameters/systemValues means). A failed call resolves
  // to {}, so the page still falls back to showing everything.
  const deviceDocumentReady =
    deviceDocument.deviceId === selectedDeviceDetails?.deviceId;

  const deviceConfig = useMemo(() => {
    if (!deviceDocumentReady) {
      return { ...selectedDeviceDetails, graphParameters: undefined, systemValues: undefined };
    }
    const { graphParameters, systemValues } = deviceDocument.data;
    const defaults = hasMeterDefaults(selectedDeviceDetails?.deviceId) ? METER_DEFAULT_CONFIG : {};
    return {
      ...selectedDeviceDetails,
      graphParameters: graphParameters ?? defaults.graphParameters,
      systemValues: systemValues ?? defaults.systemValues,
    };
  }, [selectedDeviceDetails, deviceDocument, deviceDocumentReady]);

  const [houseHasEnergySyncDevices, setHouseHasEnergySyncDevices] =
    useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { refreshing: storesRefreshing, refresh: refreshStoreList } =
    useStoreListRefresh();

  // Re-reads the selected appliance's data, which is what every tile and chart
  // on this page is showing.
  //
  // It deliberately does not reload the store's assets: fetchStoreAssets
  // dispatches houseData, and MainPage's effect on [houseData] would reset the
  // Area and Appliance dropdowns back to the first of each - so refreshing
  // would silently move the user off the device they were looking at. (This
  // previously read login.profile.mobile, which the PKCE login never sets, and
  // threw on every click.)
  const handleRefresh = useCallback(async () => {
    if (!selectedDeviceDetails?.deviceId) return;
    setRefreshing(true);
    try {
      await fetchDataForDeviceAndDate(
        selectedDeviceDetails.deviceId,
        selectedDeviceDetails.type,
        selectedDeviceDetails.assetId
      );
    } finally {
      // Without this the spinner would stay on forever if the fetch throws.
      setRefreshing(false);
    }
  }, [selectedDeviceDetails]);
  useEffect(() => {
    handleDateChange([dayjs(), dayjs()])
  }, [])
  //------------------------------------useEffect for snapshot saved in redux--------------------
  useEffect(() => {

    if (Object.keys(snapshotUnit).length === 0) return;

    const selectedDeviceId = selectedDeviceDetails
      ? selectedDeviceDetails.deviceId
      : undefined;
    const dates = findDatesBetween(selectedDate, selectedEndDate);
    setSelectedDeviceSelectedDayData([]);
    let dataOfDates = [];
    for (let date of dates) {

      const selectedDateInStringWithoutHours = new Date(
        new Date(date).setHours(0, 0, 0, 0)
      ).toDateString();
      if (
        snapshotUnit[selectedDeviceId] &&
        snapshotUnit[selectedDeviceId][selectedDateInStringWithoutHours]
      ) {
        dataOfDates.push({ ...snapshotUnit[selectedDeviceId][selectedDateInStringWithoutHours], date })
      }

    }
    if (dataOfDates.length === 0) {
      fetchDataForDeviceAndDate(
        selectedDeviceDetails.deviceId,
        selectedDeviceDetails.type,
        selectedDeviceDetails.assetId)
    }
    setSelectedDeviceSelectedDayData(dataOfDates)
    // Keyed on the device, not the selection object: restoring the saved
    // selection on mount dispatches a new object for the same device, which
    // re-ran this and fetched the same data a second time.
  }, [snapshotUnit, selectedDeviceDetails?.deviceId, selectedDeviceDetails?.type]);

  //-------------------------------useEffect to calculate System Values and Graph Data---------------------------

  useEffect(() => {
    if (selectedDeviceSelectedDayData.length === 0) return;
    if (Object.keys(selectedDeviceDetails).length === 0) return;

    const { systemValues, graphData, phaseLoadLogs } = calculateGlobalData(
      selectedDeviceSelectedDayData,
      ppu,
      deviceConfig
    );
    let saving = []

    if (deviceConfig?.graphParameters?.saving !== undefined && deviceConfig?.graphParameters?.saving?.maximumUnitPerHour !== undefined) {
      for (let dataConsume in graphData.carbonConsumed) {
        saving.push(graphData.carbonConsumed[dataConsume])
      }
    }
    let multipleDayCarbonConsumed = []
    if (Object.keys(graphData.carbonConsumed).length > 1) {
      let multipleDayPhaseLoadLogs = {}
      for (let dataConsume in graphData.carbonConsumed) {
        for (let dataConsumed of graphData.carbonConsumed[dataConsume]) {

          let name = dataConsumed.name.split("-")[1]

          if (multipleDayPhaseLoadLogs[name] === undefined) {
            multipleDayPhaseLoadLogs[name] = [{ ...dataConsumed, name: name, date: dataConsume }]
          } else {
            multipleDayPhaseLoadLogs[name] = [...multipleDayPhaseLoadLogs[name], { ...dataConsumed, name: name, date: dataConsume }]
          }
        }
      }

      for (let dataConsume in multipleDayPhaseLoadLogs) {
        let categories = []
        let data = []
        let totalSaved = 0
        let totalSavingPer = 0
        for (let dataConsumed of multipleDayPhaseLoadLogs[dataConsume]) {
          categories.push(dataConsumed.date)
          data.push(dataConsumed.data.reduce((acc, curr) => acc + curr, 0))
          totalSaved += dataConsumed.totalSaved
          totalSavingPer += dataConsumed.totalSavingPer
        }
        multipleDayCarbonConsumed.push({ name: dataConsume, categories, data, totalSaved, totalSavingPer })
      }

    } else if (Object.keys(graphData.carbonConsumed).length === 1) {
      for (let dataConsume in graphData.carbonConsumed) {
        multipleDayCarbonConsumed = [...multipleDayCarbonConsumed, ...graphData.carbonConsumed[dataConsume]]
      }
    }
   
    // netUnitLog calculation for multiple days and single day
    let multipleDayNetUnitData = []
    if (Object.keys(graphData.netUnitConsumedData).length > 1) {
         let categories = []
        let data = []
      for (let date in graphData.netUnitConsumedData) {
     
       let graphDataNet = graphData.netUnitConsumedData[date]?.barGraphData
       console.log("graphDataNet",graphData)
          categories.push(date)
          data.push(graphDataNet?.data?.reduce((acc, curr) => acc + curr, 0))
        }
        multipleDayNetUnitData.push({ name: "Net Unit Consumed", categories, data })

    } else if (Object.keys(graphData.netUnitConsumedData).length === 1) {
      for (let date in graphData.netUnitConsumedData) {
        let graphDataNet = graphData.netUnitConsumedData[date]?.barGraphData

        multipleDayNetUnitData = [...multipleDayNetUnitData, graphDataNet]
      }
    }
   

  
    setPhaseLoadLogs({ ...phaseLoadLogs });
    setSystemValuesData({ ...systemValues });
    setGraphData({ ...graphData, carbonConsumed: multipleDayCarbonConsumed, saving,netUnitConsumedData:multipleDayNetUnitData });
  }, [selectedDeviceSelectedDayData, deviceConfig]);

  //-----------------------------------Handle Date Change-----------------------------------------
  const handleDateChange = async (newValue) => {
    store.dispatch(selectedDay(newValue[0]?.$d));
    store.dispatch(selectedEndDay(newValue[1]?.$d));
    await fetchDataForDeviceAndDate(
      selectedDeviceDetails.deviceId,
      selectedDeviceDetails.type,
      selectedDeviceDetails.assetId
    );
  };

  //-----------------------------------Handle store change for store dropdown selection-------------
  // Areas become the rooms and assets become the appliances, so one assets call
  // fills both dropdowns below.
  const handleStoreChange = async (selectedStore) => {
    setLoading(true);
    setHouseHasEnergySyncDevices(true);
    const storeCode = selectedStore.code;
    const result = await fetchStoreAssets(storeCode);

    if (!result.success) {
      messages.error(result.message);
      setLoading(false);
      return;
    }

    store.dispatch(selectedHouse(storeCode));

    // The store's devices grouped by type; the first type and its first
    // device are opened.
    const groups = deviceTypeGroups(result.data.rooms ?? []);

    if (groups.length === 0) {
      setRoomCount(0);
      setHouseHasEnergySyncDevices(false);
      store.dispatch(selectedDeviceType(""));
      store.dispatch(selectedRoomDevices([]));
      setLoading(false);
      return;
    }

    setRoomCount(groups.length);

    const [defaultGroup] = groups;
    const defaultDeviceDetails = defaultGroup.devices[0];

    store.dispatch(selectedDeviceType(defaultGroup.key));
    store.dispatch(selectedRoomDevices(defaultGroup.devices));
    store.dispatch(selectedDeviceFromDropdown(defaultDeviceDetails));

    await fetchDataForDeviceAndDate(
      defaultDeviceDetails.deviceId,
      defaultDeviceDetails.type,
      defaultDeviceDetails.assetId
    );

    setLoading(false);
  };

  //-----------------------------------Handle type change for device type dropdown selection-------
  // Lists the type's devices, from every area, and opens the first.
  const handleTypeChange = async (group) => {
    const [firstDevice] = group.devices;
    if (!firstDevice) return;
    store.dispatch(selectedDeviceType(group.key));
    store.dispatch(selectedRoomDevices(group.devices));
    store.dispatch(selectedDeviceFromDropdown(firstDevice));
    await fetchDataForDeviceAndDate(firstDevice.deviceId, firstDevice.type, firstDevice.assetId);
  };

  //-----------------------------------Handle device change for device dropdown selection ----------
  const handleDeviceChange = async (event) => {
    const deviceDetails = JSON.parse(event.target.value);
    store.dispatch(selectedDeviceFromDropdown(deviceDetails));
    await fetchDataForDeviceAndDate(deviceDetails.deviceId, deviceDetails.type, deviceDetails.assetId);
  };

  //-------------------------- Common function to handle data fetching-------------------------------
  // assetId is the appliance's assetId, sent with the analysis calls.
  const fetchDataForDeviceAndDate = async (deviceId, type, assetId) => {
    const selectedDate = store.getState().selectedDate;
    const selectedEndDate = store.getState().selectedEndDay;
    const dates = findDatesBetween(selectedDate, selectedEndDate);
    setSelectedDeviceSelectedDayData([]);
    let dataOfDates = [];
    for (let date of dates) {
      const selectedDateInStringWithoutHours = new Date(date).setHours(
        0,
        0,
        0,
        0
      );
      const todayDateInStringWithoutHours = new Date().setHours(0, 0, 0, 0);

      const isDifferentDate =
        selectedDateInStringWithoutHours !== todayDateInStringWithoutHours;

      if (!isDifferentDate) {
        let dataToday = {}
        setLoading(true);
        if (type === config.deviceType.energiSync) {
          await fetchAndStoreMultipleDevicesTodayData([{ deviceId, assetId }]).then((res) => {
            if (res.length > 0) {
              dataToday = res[0];

            }
          });
        } else if (type === config.deviceType.environSyncs) {
          await environAwsApi(deviceId, todayDateInStringWithoutHours).then((res) => {
            if (Object.keys(res).length > 0) {
              dataToday = res;
            }
          })
        } else if (type === config.deviceType.thermoSmarts) {
          await thermoAwsApi(deviceId, todayDateInStringWithoutHours).then((res) => {
            if (Object.keys(res).length > 0) {
              dataToday = res;
            }
          })
        } else if (type === config.deviceType.weatherSensors) {
          await weatherAwsApi(deviceId, todayDateInStringWithoutHours).then((res) => {
            if (Object.keys(res).length > 0) {
              dataToday = res;
            }
          })
        }
        if (Object.keys(dataToday).length > 0) {
          dataOfDates.push({ ...dataToday, date });
        }
      } else {
        let dataPreviousDay = {}
        setLoading(true);
        if (type === config.deviceType.energiSync) {
          await fetchAndStoreMulipleDevicesSelectedDayData(
            [{ deviceId, assetId }],
            selectedDateInStringWithoutHours
          ).then((res) => {
            if (res.length > 0) {
              dataPreviousDay = res[0];
            }
          })
        } else if (type === config.deviceType.environSyncs) {
          await environAwsApi(deviceId, selectedDateInStringWithoutHours).then((res) => {
            if (Object.keys(res).length > 0) {
              dataPreviousDay = res;
            }
          })
        } else if (type === config.deviceType.thermoSmarts) {
          await thermoAwsApi(deviceId, selectedDateInStringWithoutHours).then((res) => {
            if (Object.keys(res).length > 0) {
              dataPreviousDay = res;
            }
          })
        } else if (type === config.deviceType.weatherSensors) {
          await weatherAwsApi(deviceId, selectedDateInStringWithoutHours).then((res) => {
            if (Object.keys(res).length > 0) {
              dataPreviousDay = res;
            }
          })
        }
        if (Object.keys(dataPreviousDay).length > 0) {
          dataOfDates.push({ ...dataPreviousDay, date });
        }
        // }
      }
    }
    setSelectedDeviceSelectedDayData(dataOfDates);
    setLoading(false);
  };


  //----------------------------------------------------------------------------------------------------
  // The summary tiles' "Last log" is only shown for today. For a past day it
  // is just that day's last reading, and the two status tiles show the
  // device as it is now, so a past time beside them would mislead.
  const rangeEndsToday =
    new Date(selectedEndDate).setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0);
  // What the total tiles add up over, said in their names so a total is not
  // read as a live value: "today", "on 12 Oct" or "in range".
  const totalsPeriod =
    selectedDeviceSelectedDayData.length > 1
      ? "in range"
      : rangeEndsToday
      ? "today"
      : `on ${dayjs(selectedEndDate).format("D MMM")}`;

  const systemValuesContainerComponents = {
    temperature: (
      <TemperatureDetailContainer
        key="temperature"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        live={rangeEndsToday}
      />
    ),
    onlineStatus: (
      <OnlineStatusDetailContainer
        key="onlineStatus"
        time={systemValuesData.time}
        showTime={rangeEndsToday}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
      />
    ),
    ACStatus: (
      <ACStatusDetailContainer
        key="ACStatus"
        time={systemValuesData.time}
        showTime={rangeEndsToday}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        deviceId={selectedDeviceDetails?.deviceId}
        // The name the Appliance dropdown shows, for the on/off confirmation.
        applianceName={selectedDeviceDetails?.name}
        // The /details document this page already read, so the tile does not
        // fetch it a second time.
        device={deviceDocumentReady ? deviceDocument.data : undefined}
      />
    ),
    applianceHealth: (
      <ApplianceHealthDetailContainer
        key="applianceHealth"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
      />
    ),
    unitConsumed: (
      <TotalUnitsConsumedDetailContainer
        key="unitConsumed"
        name={`Units Consumed ${totalsPeriod}`}
        time={systemValuesData.time}
        unitsConsumed={systemValuesData.globalUnitConsumed}
        showTime={selectedDeviceSelectedDayData.length === 1 && rangeEndsToday}
      />
    ),
    netUnitConsumed: (
       <TotalUnitsConsumedDetailContainer
        key="netUnitConsumed"
        name={`Net Units Consumed ${totalsPeriod}`}
        time={systemValuesData.time}
        unitsConsumed={systemValuesData?.globalNetUnitConsumed}
        showTime={selectedDeviceSelectedDayData.length > 1 ? false : true}
      />
    ),
    unitSaved: (
      <UnitSavedDetailContainer
        key="unitSaved"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        unitsSaved={deviceConfig?.graphParameters?.saving?.maximumUnitPerHour !== undefined ? systemValuesData.globalSaving : systemValuesData.globalUnitSaved}
      />
    ),
    moneySpent: (
      <MoneySpentDetailContainer
        key="moneySpent"
        name={`Money Spent ${totalsPeriod}`}
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        moneySpent={systemValuesData.globalMoneySpent}
        showTime={selectedDeviceSelectedDayData.length > 1 ? false : true}
      />
    ),
    netMoneySpent: (
      <MoneySpentDetailContainer
        key="netMoneySpent"
        name={`Net Money Spent ${totalsPeriod}`}
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        moneySpent={systemValuesData.globalNetMoneySpent}
        showTime={selectedDeviceSelectedDayData.length > 1 ? false : true}
      />
    ),
    moneySaved: (
      <MoneySavedDetailContainer
        key="moneySaved"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        moneySaved={systemValuesData.globalMoneySaved}
      />
    ),
    moneySaving: (
      <MoneySavingDetailContainer
        key="moneySaving"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
      />
    ),
    unitSaving: (
      <UnitSavingsDetailContainer
        key="unitSaving"
        time={systemValuesData.time}
        unitsSaved={systemValuesData.globalUnitSaved}
        unitsConsumed={systemValuesData.globalUnitConsumed}
      />
    ),
    saving: (
      <SavingsDetailContainer
        key="saving"
        time={systemValuesData.time}
        unitsSaved={systemValuesData.globalSaving}
        unitsConsumed={systemValuesData.globalUnitConsumed}
      />
    ),
    carbonSaving: (
      <CarbonSavedDetailContainer
        key="carbonSaving"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        carbonSavedData={graphData.carbonSaved}
        carbonConsumedData={graphData.carbonConsumed}
      />
    ),
    applianceOnTime: (
      <ApplianceOnTimeDetailContainer
        key="applianceOnTime"
        time={selectedDeviceSelectedDayData.length > 0 ? selectedDeviceSelectedDayData[0]?.createdAt ?? systemValuesData.time : systemValuesData.time}
        value={formatDurationHrsMinsSecs(selectedDeviceSelectedDayData.length > 0 ? selectedDeviceSelectedDayData[0]?.occupiedTime || 0 : 0)}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
      />
    )
  };
  const systemValueThermoContainerComponents = {
    onlineStatus: (
      <OnlineStatusDetailContainer
        key="onlineStatus"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
      />
    ),
    temperature: (
      <TemperatureDetailContainer
        key="temperature"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        live={rangeEndsToday}
      />
    ),
  };
  const systemValuesWeaherContainerComponents = {

    onlineStatus: (
      <OnlineStatusDetailContainer
        key="onlineStatus"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
      />
    ),
    temperature: (
      <TemperatureDetailContainer
        key="temperature"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        live={rangeEndsToday}
      />
    ),
    Humidity: (
      <WeattherValue
        key="Humidity"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
        dataKey={"humidityLogs"}
        valueKey={"h"}
        name={"Humidity"}
        image={images.chartHumidity}
        iconBackground="rgba(30, 136, 229, 0.10)"
        timeKey={"ts"}
        degree={' AH'}
      />
    ),
  };
  const systemValueEnvirnoContainerComponents = {

    onlineStatus: (
      <OnlineStatusDetailContainer
        key="onlineStatus"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
      />
    ),
    temperature: (
      <TemperatureDetailContainer
        key="temperature"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        live={rangeEndsToday}
      />
    ),
    Humidity: (
      <WeattherValue
        key="Humidity"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
        dataKey={"humidityLogs"}
        valueKey={"h"}
        name={"Humidity"}
        image={images.chartHumidity}
        iconBackground="rgba(30, 136, 229, 0.10)"
        timeKey={"ts"}
        degree={' AH'}
      />
    ),
    Co2Level: (
      <WeattherValue
        key="Co2Level"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
        dataKey={"co2Logs"}
        valueKey={"co2"}
        name={"Co2 Level"}
        image={images.chartCo2}
        iconBackground="rgba(125, 135, 155, 0.14)"
        timeKey={"ts"}
        toolTip={"Co2"}
        degree={" Co2"}
      />
    ),
    airQualityIndex: (
      <WeattherValue
        key="airQualityIndex"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
        dataKey={"aqiLogs"}
        valueKey={"aqi"}
        name={"Air Quality Index"}
        image={images.chartAirQuality}
        iconBackground="rgba(34, 163, 74, 0.10)"
        timeKey={"ts"}
        toolTip={"AQI"}
        degree={" AQI"}
      />
    ),
    equivalentCarbonDioxide: (
      <WeattherValue
        key="equivalentCarbonDioxide"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
        dataKey={"aqiLogs"}
        valueKey={"eco2"}
        name={"Equivalent CO2"}
        timeKey={"ts"}
        toolTip={"eCO2"}
        degree={" eCO2"}
      />
    ),
  };
  const systemValueDoorSensorContainerComponents = {

    onlineStatus: (
      <OnlineStatusDetailContainer
        key="onlineStatus"
        time={systemValuesData.time}
        selectedDeviceSelectedDayData={selectedDeviceDetails}
      />
    ),
    doorStatus: (
      <DoorSensor
        data={selectedDeviceDetails}
      />
    ),




  };
 
  //----------------------------------------------------------------------------------------------------------
  const chartComponents = {

    carbonSaved: (
      <CarbonSavedBarChart
        key="carbonConsumed"
        carbonSavedData={graphData.carbonSaved}
        stacked="true"
      />
    ),
     netUnitConsumed: (
      <CumUnitConsumedBarChart
        key="unitConsumed"
        carbonConsumedData={graphData.netUnitConsumedData}
        stacked="true"
      />
    ),
    carbonConsumed: (
      <CarbonConsumeBarChart
        key="carbonSaved"
        carbonConsumedData={graphData.carbonConsumed}
        stacked="true"
      />
    ),
     netCarbonConsumed: (
      <CumCarbonConsumeBarChart
        key="carbonSaved"
        carbonConsumedData={graphData?.netUnitConsumedData}
        stacked="true"
      />
    ),
    unitSaved: (
      <UnitSavedBarChart
        key="unitSaved"
        carbonSavedData={graphData.carbonSaved}
        stacked="true"
      />
    ),
    saving: (
      <SavingBarChart
        key="saving"
        carbonSavedData={graphData.saving}
        stacked="true"
      />
    ),
    unitConsumed: (
      <UnitConsumedBarChart
        key="unitConsumed"
        carbonConsumedData={graphData.carbonConsumed}
        stacked="true"
      />
    ),
   
    moneySaved: (
      <MoneySavedBarChart
        key="moneySaved"
        carbonSavedData={graphData.carbonSaved}
        ppu={ppu}
        stacked="true"
      />
    ),
    moneySpent: (
      <MoneySpentBarChart
        key="moneySpent"
        carbonConsumedData={graphData.carbonConsumed}
        ppu={ppu}
        stacked="true"
      />
    ),
    netMoneySpent: (
      <CumMoneySpentBarChart
        key="moneySpent"
        carbonConsumedData={graphData.netUnitConsumedData}
        ppu={ppu}
        stacked="true"
      />
    ),
    netKwhKvah: (
      <NetKwhKvah
        key="netKwhKvah"
        totalKwh={systemValuesData.globalNetKwh}
        totalKvah={systemValuesData.globalNetKvah}
        ppu={ppu}
        stacked="true"
      />
    ),
    temperature: (
      <TemperatureLineChart
        key="temperature"
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
      />),
    onTimeTable: (
      <EnergiSychOccupancyLogs
      data={selectedDeviceDetails}
      multipleDayData={selectedDeviceSelectedDayData}
      />
    ),
    power: (
      <PowerConsumptionLineChart
        key="power"
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
        deviceId={selectedDeviceDetails?.deviceId}
      />
    ),
     frequency: (
      <FrequencyLineChart
        key="frequency"
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
        deviceId={selectedDeviceDetails?.deviceId}
      />
    ),
    powerFactor: (
      <PowerFactorLineChart
        key="powerFactor"
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
        deviceId={selectedDeviceDetails?.deviceId}
      />
    ),
    voltage: (
      <VoltageLineChart
        key="voltage"
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        deviceId={selectedDeviceDetails?.deviceId}
        stacked="true"
      />
    ),
    current: (
      <CurrentLineChart
        key="current"
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        deviceId={selectedDeviceDetails?.deviceId}
        stacked="true"
      />
    ),

  };
  const thermoChartComponent = {
    temperature: (
      <TemperatureLineChart
        key="temperature"
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
      />
    ),
    controlLog: (
      <WeatherControllog
        key="control"
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
        dataKey={"controlLogs"}
        valueKey={"state"}
        name={"Control"}
        image={images.chartControl}
        iconBackground="rgba(32, 80, 255, 0.10)"
        timeKey={"timestamp"}
        toolTip={"State"}
        yAxis={"Command"}
      />
    ),

  };
  const weatherChartComponent = {
    temperature: (
      <TemperatureLineChart
        key="temperature"
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
      />
    ),
    Humidity: (
      <WeatherControllog
        key="Humidity"
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
        dataKey={"humidityLogs"}
        valueKey={"h"}
        name={"Humidity"}
        image={images.chartHumidity}
        iconBackground="rgba(30, 136, 229, 0.10)"
        timeKey={"ts"}
        toolTip={"Humidity"}
        yAxis={"Absolute Humidity"}
      />
    ),
  };
  const environChartComponent = {
    temperature: (
      <TemperatureLineChart
        key="temperature"
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
      />
    ),
    Humidity: (
      <WeatherControllog
        key="Humidity"
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
        dataKey={"humidityLogs"}
        valueKey={"h"}
        name={"Humidity"}
        image={images.chartHumidity}
        iconBackground="rgba(30, 136, 229, 0.10)"
        timeKey={"ts"}
        toolTip={"Humidity"}
        yAxis={"Absolute Humidity"}
      />
    ),
    Co2Level: (
      <WeatherControllog
        key="Co2Level"
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
        dataKey={"co2Logs"}
        valueKey={"co2"}
        name={"Co2 Level"}
        image={images.chartCo2}
        iconBackground="rgba(125, 135, 155, 0.14)"
        timeKey={"ts"}
        toolTip={"Co2"}
        yAxis={"Co2"}
      />
    ),
    airQualityIndex: (
      <WeatherControllog
        key="airQualityIndex"
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
        dataKey={"aqiLogs"}
        valueKey={"aqi"}
        name={"Air Quality Index"}
        image={images.chartAirQuality}
        iconBackground="rgba(34, 163, 74, 0.10)"
        timeKey={"ts"}
        toolTip={"AQI"}
        yAxis={"AQI"}
      />
    ),
    equivalentCarbonDioxide: (
      <WeatherControllog
        key="equivalentCarbonDioxide"
        selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
        stacked="true"
        dataKey={"aqiLogs"}
        valueKey={"eco2"}
        name={"Equivalent carbon dioxide"}
        image={images.chartCo2}
        iconBackground="rgba(125, 135, 155, 0.14)"
        timeKey={"ts"}
        toolTip={"eCO2"}
        yAxis={"eCO2"}
      />
    ),
  };

  // The summary tiles to show for an EnergiSync device: those its config lists
  // (or every one, without a list), less the hidden ones and, over several
  // days, those that only make sense for one. Worked out before rendering so
  // the row can size its columns to the count.
  const multiDay = selectedDeviceSelectedDayData.length > 1;
  const summaryTileKeys = (
    deviceConfig?.systemValues ?? Object.keys(systemValuesContainerComponents)
  ).filter(
    (key) =>
      systemValuesContainerComponents[key] &&
      !HIDDEN_SYSTEM_VALUES.includes(key) &&
      (!multiDay || !notShowSystemValuesWhenMultiDay.includes(key))
  );
  // The per-phase reading rows. Single day only, and only the readings the
  // device's config lists - a temperature sensor lists none of them, so it
  // gets no rows or heading even when it has phase logs.
  const devicePhases =
    selectedDeviceSelectedDayData.length === 1
      ? phaseLoadLogs[selectedDeviceDetails?.deviceId] ?? {}
      : {};
  const phaseKeys = Object.keys(devicePhases);
  const manyPhases = phaseKeys.length > 1;
  const phaseReadingKeys =
    phaseKeys.length > 0
      ? (deviceConfig?.systemValues ?? []).filter((key) => PHASE_READING_TILES[key])
      : [];
  // Each phase's own units, beside its readings, when there is more than one.
  const phaseUnitsShown = manyPhases && (deviceConfig?.systemValues ?? []).includes("unitConsumed");

  // Three tiles take a third each, four a quarter; more wrap four to a row.
  const summaryColumnWidth = summaryTileKeys.length >= 4 ? 3 : 12 / Math.max(summaryTileKeys.length, 1);

  // The Device Type and Appliance dropdowns' options: the store's devices
  // grouped by type (see selection.js), then the picked type's devices.
  const typeOptions = useMemo(() => deviceTypeGroups(houseData?.rooms ?? []), [houseData]);
  const applianceOptions = (devices ?? []).filter(Boolean);
  const selectedType = typeOptions.find((group) => group.key === deviceType) || null;
  const selectedAppliance =
    applianceOptions.find((device) => device.deviceId === selectedDeviceDetails?.deviceId) || null;
  const selectedStore = housesList.find((house) => house.code === houseId) || null;
  // The picked store's assets have loaded and it has no device:
  // the page shows NoDevicesBanner instead of tiles. Worked out from houseData
  // rather than houseHasEnergySyncDevices, which only handleStoreChange sets,
  // so it also holds after a reload.
  const storeHasNoDevices =
    !!houseId && houseData?._id === houseId && typeOptions.length === 0;

  // Opening a dropdown clears its text for a fresh search (useSearchOnOpen).
  const storeSearch = useSearchOnOpen(
    selectedStore ? `${selectedStore.code} - ${selectedStore.houseName}` : "",
    refreshStoreList
  );
  // A type by name; its appliance count is on the summary card's chips.
  const typeLabel = (group) => (group ? group.label : "");
  const typeSearch = useSearchOnOpen(typeLabel(selectedType));
  const applianceSearch = useSearchOnOpen(
    selectedAppliance
      ? `${selectedAppliance.name ? `${selectedAppliance.name} - ` : ""}${selectedAppliance.deviceId}`
      : ""
  );

  //-----------------------------------------Main---------------------------------------------------------
  return (
    <div style={{ ...styles.container }}>
      <Toaster />
      {/*-----------------------------------Dropdown Section------------------------------------------*/}

      {/*-----------------------Filters: date, store, area, appliance-------------------------*/}
      <div className="dash-filters">
        <div className="dash-field dash-field-date">
          <span className="dash-label">Date</span>
          <DateRangePicker
            startDate={selectedDate}
            endDate={selectedEndDate}
            onDateChange={handleDateChange}
          />
        </div>

        {/*-----------------------Store Selection-------------------------*/}
        <div className="dash-field dash-field-store">
          <span className="dash-label">Store</span>
          <Autocomplete
            id="house-select"
            size="small"
            // Opening clears the text for a fresh search, and re-reads the
            // store list so a store added or renamed elsewhere shows up.
            inputValue={storeSearch.inputValue}
            onInputChange={storeSearch.onInputChange}
            onOpen={storeSearch.onOpen}
            onClose={storeSearch.onClose}
            onBlur={storeSearch.onBlur}
            loading={storesRefreshing}
            // Copy before sorting: sort() is in place, and housesList is
            // the array held in the redux store.
            options={[...housesList].sort((a, b) =>
              a.houseName
                .toLowerCase()
                .localeCompare(b.houseName.toLowerCase())
            )}
            getOptionLabel={(option) => `${option.code} - ${option.houseName}` || ""}
            value={housesList.find(house => house.code === houseId) || null}
            onChange={(event, newValue) => {
              if (newValue) {
                handleStoreChange(newValue);
              }
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder={storeSearch.placeholder || "Search stores..."} />
            )}
            isOptionEqualToValue={(option, value) =>
              option.code === value.code
            }
            sx={filterFieldSx}
          />
        </div>

        {/*-----------------------Device Type Selection-------------------------*/}
        {/* The store's device types (as Device Master sets them); picking one
            lists its appliances from every area. Searchable, like Store.
            Shown even for a single type. */}
        {roomCount > 0 && (
          <div className="dash-field">
            <span className="dash-label" id="type-select-label">Device Type</span>
            <Autocomplete
              id="type-select"
              size="small"
              disableClearable
              options={typeOptions}
              getOptionLabel={typeLabel}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              value={selectedType}
              onChange={(event, group) => {
                if (group) handleTypeChange(group);
              }}
              inputValue={typeSearch.inputValue}
              onInputChange={typeSearch.onInputChange}
              onOpen={typeSearch.onOpen}
              onClose={typeSearch.onClose}
              onBlur={typeSearch.onBlur}
              noOptionsText="No matching device type"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={typeSearch.placeholder || "Search device types..."}
                  inputProps={{ ...params.inputProps, "aria-labelledby": "type-select-label" }}
                />
              )}
              sx={filterFieldSx}
            />
          </div>
        )}

        {/*-----------------------Device/Appliance Selection-------------------------*/}
        {houseHasEnergySyncDevices && !storeHasNoDevices && (
          <div className="dash-field dash-field-appliance">
            <span className="dash-label" id="device-select-label">Appliance</span>
            <Autocomplete
              id="device-select"
              size="small"
              disableClearable
              options={applianceOptions}
              // Name and id, so typing either finds the appliance.
              getOptionLabel={(device) =>
                device ? `${device.name ? `${device.name} - ` : ""}${device.deviceId}` : ""
              }
              isOptionEqualToValue={(option, value) => option.deviceId === value.deviceId}
              value={selectedAppliance}
              onChange={(event, device) => {
                if (device) handleDeviceChange({ target: { value: JSON.stringify(device) } });
              }}
              inputValue={applianceSearch.inputValue}
              onInputChange={applianceSearch.onInputChange}
              onOpen={applianceSearch.onOpen}
              onClose={applianceSearch.onClose}
              onBlur={applianceSearch.onBlur}
              // A type spans areas, so each appliance says where it is.
              renderOption={(props, device) => (
                <li {...props} key={device.deviceId}>
                  <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                    <span>{device.name ? `${device.name} - ` : ""}{device.deviceId}</span>
                    {device.roomName && (
                      <span style={{ fontSize: 12, color: "#8A8F96" }}>{device.roomName}</span>
                    )}
                  </div>
                </li>
              )}
              noOptionsText="No matching appliance"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={applianceSearch.placeholder || "Search appliances..."}
                  inputProps={{ ...params.inputProps, "aria-labelledby": "device-select-label" }}
                />
              )}
              sx={filterFieldSx}
            />
          </div>
        )}

        {/*-----------------------Refresh Button-------------------------*/}
        <Tooltip title="Refresh data">
          <span>
            <IconButton
              onClick={handleRefresh}
              disabled={refreshing || !selectedDeviceDetails?.deviceId}
              aria-label="Refresh data"
              sx={{
                width: 40,
                height: 40,
                border: "1px solid #E9ECEF",
                borderRadius: "10px",
                backgroundColor: "#FFFFFF",
                color: "#495057",
                "&:hover": { backgroundColor: "#F1F4FB", borderColor: "#C7D0E0" },
              }}
            >
              <RefreshIcon
                fontSize="small"
                sx={{
                  transition: "transform 0.6s ease",
                  transform: refreshing ? "rotate(360deg)" : "rotate(0deg)",
                }}
              />
            </IconButton>
          </span>
        </Tooltip>

        {/*-----------------------Aliste Specific Selection-------------------------*/}
        {/* {login.userName === "aliste" && (
          <>
            <Grid item xs={8} md={2} sx={{ marginTop: "8px" }}>
              <TextField
                id="outlined-basic"
                label="Device id"
                variant="outlined"
                onChange={(e) => setDeviceId(e.target.value)}
                value={deviceId}
              />
            </Grid>
            <Grid item xs={8} md={2} sx={{ marginTop: "8px" }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  if (
                    new Date(dateA).setHours(0, 0, 0, 0) !==
                    new Date().setHours(0, 0, 0, 0)
                  ) {
                    fetchAndStoreMulipleDevicesSelectedDayData([{ deviceId: deviceId }], dateA);
                    if (selectedDeviceId === "M240002") {
                      loadDataofDevice2Day("M250001", dateA);
                    }
                  } else {

                    fetchAndStoreMultipleDevicesTodayData([{ deviceId: deviceId }]);
                    if (selectedDeviceId === "M240002") {
                      loadDataofDevice2("M250001");
                    }
                  }
                }}
              >
                Get Details
              </Button>
            </Grid>
          </>
        )} */}
      </div>

      {/* The store's appliance count, by device type. */}
      {!storeHasNoDevices && (
        <StoreApplianceSummary groups={typeOptions} />
      )}

      {/* The appliance's schedules, for appliances set to be scheduled
          (Device Master's flag on the asset). Its /details document carries
          the schedule, so this waits for that. */}
      {!storeHasNoDevices &&
        deviceDocumentReady &&
        isToBeScheduled(selectedDeviceDetails) && (
          <ScheduleStrip document={deviceDocument.data} />
        )}

      {storeHasNoDevices && (
        <NoDevicesBanner />
      )}

      {selectedDeviceSelectedDayData.length !== 0 && !storeHasNoDevices &&
        houseHasEnergySyncDevices && deviceDocumentReady && (
          <>
            {/*----------------------------------System Values Section------------------------------------------*/}
            {/* Energy Sync */}
            {selectedDeviceDetails.type === config.deviceType.energiSync && (
              <>
{/* 
              {
                console.log(selectedDeviceDetails,"selectedDeviceDetails?.systemValues",selectedDeviceSelectedDayData.length,Object.keys(systemValuesContainerComponents).filter((value) => selectedDeviceSelectedDayData.length > 1 && !notShowSystemValuesWhenMultiDay.includes(value)))
              } */}
                {/* The device's summary tiles share the row evenly (three
                    fill it, as do four), wrapping four to a row past that.
                    16px below the card above, as the cards above are spaced:
                    the tiles' own g-4 gutter adds its 24px on top of this. */}
                <Row
                  style={{ marginTop: "calc(16px - var(--bs-gutter-y))" }}
                  className="row g-4"
                >
                  {summaryTileKeys.map((key) => (
                    <Col sm={12} md={summaryColumnWidth} key={key}>
                      {systemValuesContainerComponents[key]}
                    </Col>
                  ))}
                </Row>

                {/* The device's latest readings, one row per phase, its readings
                    side by side (Voltage, Power Factor, Power, Current, Units
                    Consumed), so the phases line up as a grid with no gaps.
                    Only the readings the device's config lists are shown; a
                    device that lists none (a temperature sensor) gets no rows.
                    Headed, and each row labelled with its phase, so they read
                    as the device's last values rather than as totals. */}
                {phaseReadingKeys.length > 0 && (
                  <div style={styles.readingsHead}>
                    <div style={styles.readingsTitle}>
                      {rangeEndsToday ? "Latest readings" : "Last readings of the day"}
                    </div>
                    <div style={styles.readingsSubtitle}>
                      {rangeEndsToday
                        ? "The most recent values the device sent. Each card shows when its reading was taken."
                        : `The last values the device sent on ${dayjs(selectedEndDate).format("D MMM YYYY")}.`}
                    </div>
                  </div>
                )}
                {(phaseReadingKeys.length > 0 || phaseUnitsShown) &&
                  phaseKeys.map((phase) => {
                    const { data = [], unit } = devicePhases[phase] || {};
                    return (
                      <React.Fragment key={`${selectedDeviceDetails.deviceId}-${phase}`}>
                      {manyPhases && (
                        <div
                          style={{
                            ...styles.phaseLabel,
                            ...(phase === phaseKeys[0] && styles.firstPhaseLabel),
                          }}
                        >
                          Phase {Number(phase) + 1}
                        </div>
                      )}
                      {/* Under a label the row keeps g-4's negative top margin,
                          which cancels the cards' own gutter, so the label sits
                          on its cards rather than nearer the row above. */}
                      <Row
                        style={manyPhases ? undefined : { ...styles.smallBoxContainer }}
                        className={manyPhases ? "row g-4" : "row g-4 mt-0"}
                      >
                        {phaseReadingKeys.map((value) => {
                          const Component = PHASE_READING_TILES[value];
                          return (
                            <Col xs={12} sm={6} lg key={`${phase}-${value}`}>
                              <Component
                                phaseLoadLogs={phaseLoadLogs}
                                phaseWiseData={data}
                                phase={phase}
                                deviceId={selectedDeviceDetails.deviceId}
                                multipleDaysLogs={selectedDeviceSelectedDayData}
                                phaseInName={false}
                                live={rangeEndsToday}
                              />
                            </Col>
                          );
                        })}
                        {phaseUnitsShown && (
                          <Col xs={12} sm={6} lg>
                            <UnitConsumedDetailContainer
                              phaseLoadLogs={phaseLoadLogs}
                              phaseWiseData={data}
                              phase={phase}
                              deviceId={selectedDeviceDetails.deviceId}
                              unit={unit}
                              multipleDaysLogs={selectedDeviceSelectedDayData}
                              name={`Units Consumed ${totalsPeriod}`}
                            />
                          </Col>
                        )}
                      </Row>
                      </React.Fragment>
                    );
                  })}

              </>
            )}
            {/* Thermo Chart */}
            {selectedDeviceDetails.type === config.deviceType.thermoSmarts && (
              <>
                <Row style={{ ...styles.smallBoxContainer }}>
                  {Object.keys(systemValueThermoContainerComponents).map((key) =>
                    <Col md={3} sm={12}>{systemValueThermoContainerComponents[key]}</Col>
                  )}
                </Row>
                <Row style={{ ...styles.smallBoxContainer }}>

                  <ThermoSmartStateSystemValue
                    key="control"
                    time={systemValuesData.time}
                    selectedDeviceSelectedDayData={selectedDeviceSelectedDayData}
                    dataKey={"controlLogs"}
                    valueKey={"state"}
                    name={"Control"}
        image={images.chartControl}
        iconBackground="rgba(32, 80, 255, 0.10)"
                  />

                </Row>
              </>
            )}


            {/* Weather Chart */}
            {selectedDeviceDetails.type === config.deviceType.weatherSensors && (
              <Row style={{ ...styles.smallBoxContainer }} className="row g-4">
                {Object.keys(systemValuesWeaherContainerComponents).map((key) =>
                  <Col md={3} sm={12}>{systemValuesWeaherContainerComponents[key]}</Col>
                )}
              </Row>
            )}

            {/* Environ Chart */}
            {selectedDeviceDetails.type === config.deviceType.environSyncs && (
              <Row style={{ ...styles.smallBoxContainer }} className="row g-4">
                {Object.keys(systemValueEnvirnoContainerComponents).map((key) =>
                  <Col md={3} sm={12}>{systemValueEnvirnoContainerComponents[key]}</Col>
                )}
              </Row>
            )}
            {
              selectedDeviceDetails.type !== config.deviceType.doorSensors &&
              <>
                {/*-----------------------------------Charts Section------------------------------------------*/}
                {/* Energy Sync */}
                {selectedDeviceDetails.type === config.deviceType.energiSync && (
                  <Row style={{ marginBottom: "20px" }}>
                    {deviceConfig?.graphParameters
                      ? Object.keys(deviceConfig.graphParameters).length >
                        0
                        ? Object.keys(chartComponents).map((key) =>
                          deviceConfig.graphParameters.hasOwnProperty(
                            key
                          ) ? (
                            <Col key={key} md={6} sm={12}>
                              <div style={{ ...styles.chartContainer }} key={key}>
                                {chartComponents[key]}
                              </div>
                            </Col>
                          ) : null
                        )
                        : null
                      : Object.keys(chartComponents).map((key) => (
                        <Col key={key} md={6} sm={12}>
                          <div style={{ ...styles.chartContainer }} key={key}>
                            {chartComponents[key]}
                          </div>
                        </Col>
                      ))}
                  </Row>
                )}

                {/* Thermo Chart */}
                {selectedDeviceDetails.type === config.deviceType.thermoSmarts && (
                  <Row style={{ marginBottom: "20px" }}>
                    {Object.keys(thermoChartComponent).map((key) => (
                      <Col key={key} md={6} sm={12}>
                        <div style={{ ...styles.chartContainer }}>
                          {thermoChartComponent[key]}
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}

                {/* Weather Chart */}
                {selectedDeviceDetails.type ===
                  config.deviceType.weatherSensors && (
                    <Row style={{ marginBottom: "20px" }}>
                      {Object.keys(weatherChartComponent).map((key) => (
                        <Col key={key} md={6} sm={12}>
                          <div style={{ ...styles.chartContainer }}>
                            {weatherChartComponent[key]}
                          </div>
                        </Col>
                      ))}
                    </Row>
                  )}

                {/* Environ Syncs Chart */}
                {selectedDeviceDetails.type === config.deviceType.environSyncs && (
                  <Row style={{ marginBottom: "20px" }}>
                    {Object.keys(environChartComponent).map((key) => (
                      <Col key={key} md={6} sm={12}>
                        <div style={{ ...styles.chartContainer }}>
                          {environChartComponent[key]}
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}
              </>
            }
          </>
        )
      }

      {/* Door Sensor */}
      <>
        {/* Door Sensor System Value */}
        {selectedDeviceDetails.type === config.deviceType.doorSensors && (
          <Row style={{ ...styles.smallBoxContainer }} className="row g-4">
            {Object.keys(systemValueDoorSensorContainerComponents).map((key) =>
              <Col md={3} sm={12} key={key}>{systemValueDoorSensorContainerComponents[key]}</Col>
            )}
          </Row>
        )}
        {
          selectedDeviceDetails?.type === config?.deviceType?.doorSensors && <DoorSensorTable data={selectedDeviceDetails} selectedDate={selectedDate} selectedEndDate={selectedEndDate} />
        }
      </>
    </div >
  );
}

export default MainV2;
